/* eslint-disable simple-import-sort/imports */
import mapboxgl from 'mapbox-gl';
import React, { Suspense, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { useLocation, Redirect } from 'react-router';
import { ToastContainer } from 'react-toastify';
import { MotionConfig, useReducedMotion } from 'motion/react';

import '@mantine/core/styles.css';
import '@mantine/carousel/styles.css';
import '@mantine/tiptap/styles.css';
import '@feathr/components/dist/style/theme/index.css';
import '@feathr/components/dist/style/global/index.css';
import * as styles from './App.css';

// Load after theme/index.css.
import { usePostHog } from '@posthog/react';
import * as Sentry from '@sentry/react';

import type { User } from '@feathr/blackbox';
import { Spinner } from '@feathr/components';
import { useSupportChatAnalytics } from '@feathr/extender/hooks';
import { useAccount, useStore, useUser } from '@feathr/extender/state';
import { logUserEvents, moment } from '@feathr/hooks';
import { DefaultLanguage, i18n } from '@feathr/locales';

import Routes from './Routes';
import UserErrorBoundary from '../components/UserErrorBoundary';
import CommandPalette from '../components/CommandPalette';
import { RealtimeWrapper } from './FundraisePage';
import type { JSX } from 'react';
import { NoUserError } from '../state';
import AppLayout from './AppLayout';

async function addRecent(user: User, accountId: string): Promise<void> {
  // Prepend accountId to recents, and make sure there are no duplicate entries.
  const currentRecents = user.get('recent_account_ids', []).filter((item) => item !== accountId);
  const patch = {
    account: accountId,
    recent_account_ids: [accountId, ...currentRecents].slice(0, 20),
  };
  // Use account context (the collection default), not getHeaders(false): the PATCH echoes the
  // server-computed `role`, which serializes to null without an AccountId header for an
  // account-scoped role -- and merging that clobbers the cached role (isSuperuser) until a
  // reload. recent_account_ids/account are user-global, so this only fixes the echoed role.
  await user.patch(patch);
  if (user.isErrored) {
    Sentry.captureException(user.error, { tags: { feature: 'account-recents' } });
  }
}

function App(): JSX.Element {
  // Prefetch account and user.
  const account = useAccount({ required: false });
  const user = useUser({ required: false });
  const shouldReduceMotion = useReducedMotion();
  const posthog = usePostHog();

  /*
   * User is returned as ephemeral on the first render, so we
   * cannot use assertUser
   */
  if (!user || user.isErrored) {
    throw new NoUserError();
  }

  const location = useLocation();
  const [isRecentUpdated, setRecentUpdated] = useState<boolean>(false);
  const { UsageReports } = useStore();

  const userId = user?.id;
  const accountName = account && account.get('name');
  const userName = user?.get('name');
  const userEmail = user?.get('email');
  const darkMode = user?.getSetting('dark_mode');
  const usage = UsageReports.get(sessionStorage.getItem('accountId')!);

  useSupportChatAnalytics(account?.id);

  React.useEffect(() => {
    // Set data-theme attribute on document root
    document.documentElement.setAttribute('data-theme', darkMode ? 'dark' : 'light');
  }, [darkMode]);

  React.useEffect(() => {
    mapboxgl.accessToken = MAPBOX_ACCESS_TOKEN;
  }, []);

  React.useEffect(() => {
    window.Appcues?.page();
  }, [location.pathname, location.search, location.hash, location.state]);

  React.useEffect(() => {
    window.dataLayer = window.dataLayer || [];

    if (account && user && user.get('email')) {
      window.dataLayer.push({
        event: 'session:start',
        email: user.get('email', ''),
        name: user.name,
        d_c: account.get('date_created', ''),
        account_id: account.id || '',
      });
    }
  }, [account, account?.isPending, user, user?.isPending]);

  React.useEffect(() => {
    if (account && !account.isPending && user && user.id && usage && !usage.isPending) {
      const userProperties = {
        'Account ID': account.id,
        Account: account.get('name'),
        'Has active Superpixel': account.hasActiveSuperpixel,
        'Has services': account.hasServices,
        'Plan tier': account.activePackage?.name,
        'User role': user.isAdmin || user.isSudoer ? 'admin' : 'user',
        'Service packages': account
          .get('license')
          .packages.map(({ name }) => name)
          .join(','),
        ...usage.getReport({ labels: true }),
      };

      const licensePrice = account.activePackage?.price ?? 0;
      const totalActiveServicesPrice = account.activeServices?.reduce(
        (acc, service) => acc + service.price,
        0,
      );
      const monthlySpend = (licensePrice + totalActiveServicesPrice) / 12;

      const cannyProps = {
        appID: '66a93e0dff4d55e0e962ecd2',
        user: {
          id: user.id,
          name: user.name,
          created: user.dateCreated,
          email: user.get('email'),
          companies: [
            {
              id: account.id,
              created: account.get('date_created'),
              name: account.get('name'),
              monthlySpend,
              customFields: {
                activeServices: account.activeServices?.map(({ name }) => name).join(','),
                activeServicesPrice: totalActiveServicesPrice,
                csm: account.get('csm'),
                dateLastModified: account.get('date_last_modified'),
                license: account.activePackage?.name,
                licenseStart: account.activePackage?.period.start,
                licenseEnd: account.activePackage?.period.end,
                licensePrice: licensePrice,
              },
            },
          ],
          customFields: {
            lastAccess: user.get('date_last_access'),
            numLogins: user.get('logins'),
            role: user.get('role')?.name,
          },
        },
      };

      posthog?.register({ environment: SHRIKE_ENV || 'local' });

      posthog?.identify(
        user.id,
        {
          email: user.get('email'),
          name: user.name,
          is_admin: user.isAdmin || user.isSudoer,
          date_created: user.dateCreated,
          date_last_login: user.get('date_last_access'),
          accountId: account.id,
          accountName: account.get('name'),
          planTier: account.activePackage?.name,
          userRole: user.isAdmin || user.isSudoer ? 'admin' : 'user',
          lastAccess: user.get('date_last_access'),
          roleName: user.get('role')?.name,
          environment: SHRIKE_ENV || 'local',
        },
        {
          $set_once: {
            createdAt: user.dateCreated,
          },
        },
      );

      if (account.id) {
        posthog?.group('company', account.id, {
          name: account.get('name'),
          dateCreated: account.get('date_created'),
          planTier: account.activePackage?.name,
          hasActiveSuperpixel: account.hasActiveSuperpixel,
          hasServices: account.hasServices,
          monthlySpend,
          servicePackages: account.activeServices?.map(({ name }) => name).join(','),
          csm: account.get('csm'),
          licenseStart: account.activePackage?.period.start,
          licenseEnd: account.activePackage?.period.end,
          licensePrice,
          integrations: account.get('integrations'),
          is_internal: account.get('is_internal'),
          total_spend_current_license: usage?.get('total_spend_current_license'),
          total_conversion_value_current_license: usage?.get(
            'total_conversion_value_current_license',
          ),
          num_conversions_current_license: usage?.get('num_conversions_current_license'),
          num_identified_persons: usage?.get('num_identified_persons'),
          num_reachable_people: usage?.get('num_reachable_people'),
          num_users: usage?.get('num_users'),
          last_campaign_publish_date: usage?.get('last_campaign_publish_date'),
        });
      }

      posthog?.capture('user_login', {
        user_id: user.id,
        account_id: account.id,
      });

      /*
       * Identify user in Canny.
       * https://developers.canny.io/install/custom-fields
       */
      window.Canny('identify', cannyProps);

      // Appcues.identify() automatically calls Appcues.page().
      window.Appcues?.identify(
        // Unique, required
        user.id,
        userProperties,
      );
      if (account.hasActiveSuperpixel) {
        logUserEvents({ ['Superpixel is active']: null });
      }
    }
  }, [user, userId, account, accountName, UsageReports, usage, posthog]);

  React.useEffect(() => {
    if (user && !user.isPending) {
      // Appcues.identify() automatically calls Appcues.page().
      window.Appcues?.identify(
        // Unique, required
        user.id,
        {
          user_id: user.id,
          user_name: user.name,
          email: user.get('email'),
          // Unix timestamp
          created_at: moment.utc(user.dateCreated).unix(),
          logins_count: user.get('logins'),
        },
      );
    }
  }, [user, userName]);

  React.useEffect(() => {
    if (user && !user.isPending && userEmail) {
      window.plantrack?.identify(null, { email: userEmail });
    }
  }, [user, userEmail]);

  React.useEffect(() => {
    if (user && !user.isPending) {
      const lng = user.getSetting('lng', DefaultLanguage);
      if (i18n.language !== lng) {
        // TODO: User language setting should not override language in querystring
        i18n.changeLanguage(lng);
        document.documentElement.lang = lng;
      }
    }
  }, [user, userName]);

  if (user.isPending || (account && account.isPending)) {
    return <Spinner className={styles.spinner} />;
  } else if (!user.isPending && user.get('account') && !account) {
    // If we have no account context, it means there was no valid account id prefixing the route
    return (
      <Redirect
        to={{
          pathname: `/${user.get('account')}/${location.pathname.split('/').slice(1).join('/')}`,
          search: location.search,
          state: { from: location },
        }}
      />
    );
  } else if (
    !user.isPending &&
    account?.isErrored &&
    !account.isFetched &&
    !location.pathname.endsWith('/404-not-found')
  ) {
    /*
     * isErrored && !isFetched = a no-access fetch (403/404) -> 404. A failed mutation
     * leaves isFetched true, so we stay put and let the page show its own error. The
     * path guard stops this re-firing once on /404-not-found (account stays errored)
     * and looping history.replace via the changing `state` (React #185).
     */
    return (
      <Redirect
        to={{ pathname: `/${user.get('account')}/404-not-found`, state: { from: location } }}
      />
    );
  }

  if (account && !account.isEphemeral && account.id && !isRecentUpdated) {
    const recents = user.get('recent_account_ids', []);
    if (account.id !== sessionStorage.getItem('accountId')) {
      // Store accountId in sessionStorage to use in Session.getHeaders().
      sessionStorage.setItem('accountId', account.id);
    }
    if (recents.length === 0 || (recents.length >= 1 && recents[0] !== account.id)) {
      void addRecent(user, account.id);
      setRecentUpdated(true);
    }
  }

  return (
    <UserErrorBoundary>
      <RealtimeWrapper>
        <CommandPalette>
          <MotionConfig
            reducedMotion={shouldReduceMotion ? 'always' : 'never'}
            transition={{ duration: 0.3 }}
          >
            <AppLayout>
              <Suspense fallback={<Spinner />}>
                <Routes />
                <ToastContainer pauseOnFocusLoss={false} />
              </Suspense>
            </AppLayout>
          </MotionConfig>
        </CommandPalette>
      </RealtimeWrapper>
    </UserErrorBoundary>
  );
}

export default observer(App);
